import { Outlet, useNavigate } from 'react-router-dom'

import { ADMIN_PATHS } from '../app/routes.js'
import { useI18n } from '../lib/i18n.jsx'
import { useIsNarrow } from '../lib/viewport.js'
import { Button, EmptyState } from '../ui/index.js'

/**
 * Guard for the console pages that cannot honestly be used on a phone.
 *
 * **Why a route guard and not CSS.** Hiding the editor with `display: none`
 * hides the pixels and nothing else: the component still mounts, its
 * autosave timer still fires every second and a half, and its `beforeunload`
 * handler still arms. Refusing at the route means `RecordEditor` never mounts,
 * so none of that starts. This is the difference between "you cannot see it"
 * and "it is not running".
 *
 * **Why the sidebar links stay visible.** The console already hides entries an
 * editor may not use, and that hiding means *you do not have permission*.
 * Reusing it for "your window is narrow" would say the wrong thing in the
 * console's own vocabulary — someone signing in from a phone would read their
 * missing entries as an account that had been demoted. A visible link that
 * explains itself when tapped is the smaller surprise.
 *
 * **Why it explains rather than redirects.** Bouncing a phone visitor to the
 * dashboard would throw away the address they opened — often a link somebody
 * sent them — and leave them with no idea what happened. Standing still and
 * saying why costs one screen and answers the question.
 *
 * **What this is not.** It is a usability measure, not access control. Anyone
 * can widen a window, and the server does not care how wide the browser was.
 * Every route behind here is still guarded on the server; nothing about this
 * file is load-bearing for security.
 *
 * 守卫那些在手机上无法诚实使用的后台页面。
 *
 * **为什么用路由守卫而不是 CSS。** 用 `display: none` 把编辑器藏起来，藏掉的只有像素：
 * 组件照样挂载，它的自动保存定时器照样每 1.5 秒触发一次，`beforeunload` 监听照样装上。
 * 在路由层拒绝，意味着 `RecordEditor` 根本不会挂载，上述一切都不会启动。
 * 这是「你看不见它」和「它没在运行」的区别。
 *
 * **为什么侧栏入口仍然可见。** 后台本来就会隐藏编辑不可用的条目，而那种隐藏的含义是
 * **你没有权限**。把它复用到「你的窗口太窄」上，等于用后台自己的词汇说了另一件事——
 * 从手机登录的人会把消失的条目读成「我的账号被降权了」。
 * 留着入口、点进去给一句解释，是更小的惊吓。
 *
 * **为什么是解释而不是跳转。** 把手机访客弹回概览，会丢掉他们打开的那个地址——
 * 那往往是别人发给他们的链接——而且完全不告诉他们发生了什么。
 * 停在原地说清缘由，代价是一屏，换来的是问题被回答。
 *
 * **它不是什么。** 这是可用性措施，不是访问控制。谁都可以把窗口拉宽，
 * 服务端也根本不关心浏览器有多宽。这后面的每条路由在服务端仍然各自设防；
 * 本文件不承担任何安全职责。
 *
 * @returns {JSX.Element} The child route, or an explanation / 子路由，或一段解释。
 */
export default function RequireWideViewport() {
  const isNarrow = useIsNarrow()
  const { t } = useI18n()
  const navigate = useNavigate()

  if (!isNarrow) return <Outlet />

  return (
    <EmptyState
      title={t('admin.narrowTitle')}
      body={t('admin.narrowBody')}
      action={
        <Button size="sm" onClick={() => navigate(ADMIN_PATHS.dashboard)}>
          {t('admin.narrowAction')}
        </Button>
      }
    />
  )
}
